// components/ui/MentorCard.jsx
import React from 'react';
import PropTypes from 'prop-types';

const MentorCard = ({ mentor }) => {
  return (
    <div className="bg-white rounded-xl border border-[#E2EFEA] hover:border-[#249E94]/40 hover:shadow-sm transition-all duration-300 p-6">
      <div className="flex items-start space-x-4 mb-6">
        {/* Avatar with Image */}
        <div className="relative">
          {mentor.hasImage && mentor.image ? (
            <div className="w-16 h-16 rounded-full overflow-hidden">
              <img 
                src={mentor.image}
                alt={mentor.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="${mentor.color} w-16 h-16 rounded-full flex items-center justify-center">
                      <span class="text-white text-xl font-bold">${mentor.initials}</span>
                    </div>
                  `;
                }}
              />
            </div>
          ) : (
            <div className={`${mentor.color} w-16 h-16 rounded-full flex items-center justify-center`}>
              <span className="text-white text-xl font-bold">{mentor.initials}</span>
            </div>
          )}
          {/* Availability indicator */}
          <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${mentor.available ? 'bg-[#3BC1A8]' : 'bg-[#64748B]'}`} />
        </div>
        
        <div className="flex-1">
          <h3 className="text-xl font-bold mb-1 text-[#0F172A]">{mentor.name}</h3>
          <p className="text-[#64748B] text-sm mb-2">{mentor.title}</p>
          <div className="flex items-center text-[#64748B] text-sm">
            <svg className="w-4 h-4 mr-1 text-[#0C7779]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            {mentor.location}
          </div>
        </div>
      </div>

      <p className="text-[#64748B] mb-6 text-sm">{mentor.bio}</p>

      {/* Expertise */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3 text-[#0F172A] text-sm">Expertise</h4>
        <div className="flex flex-wrap gap-2">
          {mentor.expertise.map((skill, idx) => (
            <span key={idx} className="px-3 py-1 bg-[#F4FAF9] text-[#0C7779] rounded-full text-xs">
              {skill}
            </span>
          ))}
        </div>
      </div>

      {/* What you'll learn */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3 text-[#0F172A] text-sm">What You'll Learn</h4>
        <ul className="space-y-2">
          {mentor.learnings.map((item, idx) => (
            <li key={idx} className="flex items-start">
              <svg className="w-4 h-4 text-[#3BC1A8] mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-[#64748B] text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Format */}
      <div className="mb-6">
        <h4 className="font-semibold mb-3 text-[#0F172A] text-sm">Format</h4>
        <div className="flex flex-wrap gap-2">
          {mentor.format.map((format, idx) => (
            <span key={idx} className="px-3 py-1 bg-[#F4FAF9] text-[#0C7779] rounded-full text-xs">
              {format}
            </span>
          ))}
        </div>
      </div>

      {/* Book Session Button */}
      <button className={`w-full py-3 rounded-lg font-semibold text-sm transition-colors ${mentor.available ? 'bg-[#249E94] text-white hover:bg-[#1D867C]' : 'bg-[#F4FAF9] text-[#64748B] cursor-not-allowed'}`}>
        {mentor.available ? 'Book a Session' : 'Currently Unavailable'}
      </button>
    </div>
  );
};

MentorCard.propTypes = {
  mentor: PropTypes.shape({
    name: PropTypes.string.isRequired,
    initials: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    expertise: PropTypes.arrayOf(PropTypes.string).isRequired,
    location: PropTypes.string.isRequired,
    bio: PropTypes.string.isRequired,
    learnings: PropTypes.arrayOf(PropTypes.string).isRequired,
    format: PropTypes.arrayOf(PropTypes.string).isRequired,
    available: PropTypes.bool.isRequired,
    color: PropTypes.string.isRequired,
    hasImage: PropTypes.bool,
    image: PropTypes.string
  }).isRequired
};

export default MentorCard;